/**
 * 注册页。
 *
 * 邮箱 + 密码 + 昵称注册，成功后自动登录并跳转首页。
 *
 * @module pages/auth/RegisterPage
 */
import { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import { register, login as loginService } from '@/services/auth';

/**
 * 注册页组件。
 *
 * @returns 注册表单元素。
 */
export default function RegisterPage(): JSX.Element {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await register({ email, password, name: name || undefined });
      // 注册成功后自动登录并进入首页。
      await loginService(email, password);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : '注册失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <TextField
        label="昵称"
        fullWidth
        margin="normal"
        value={name}
        onChange={(e) => setName(e.target.value)}
        autoComplete="nickname"
      />
      <TextField
        label="邮箱"
        type="email"
        fullWidth
        required
        margin="normal"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        autoComplete="email"
      />
      <TextField
        label="密码"
        type="password"
        fullWidth
        required
        margin="normal"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoComplete="new-password"
      />
      <Button
        type="submit"
        variant="contained"
        fullWidth
        size="large"
        disabled={loading}
        sx={{ mt: 2 }}
      >
        {loading ? '注册中...' : '注册'}
      </Button>
      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          已有账号？{' '}
          <Link component={RouterLink} to="/login">
            返回登录
          </Link>
        </Typography>
      </Box>
    </Box>
  );
}
